import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, Play, ShieldCheck, Lock, MapPin, Sparkles } from "lucide-react";
import { SITE, TRUST_BADGES } from "@/data/site";
import PhoneMockup from "./PhoneMockup";

const ICONS = { ShieldCheck, Lock, MapPin, Sparkles };

function StoreButton({ icon: Icon, top, bottom, testid }) {
    return (
        <a
            href="#"
            data-testid={testid}
            className="flex items-center gap-3 rounded-2xl bg-gr-ink px-5 py-3 text-white transition-transform hover:-translate-y-0.5 active:scale-95 dark:bg-white dark:text-gr-ink"
        >
            <Icon className="h-6 w-6" />
            <span className="text-left leading-none">
                <span className="block text-[10px] opacity-70">{top}</span>
                <span className="block font-heading text-base font-bold">{bottom}</span>
            </span>
        </a>
    );
}

export default function Hero() {
    return (
        <section className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 grain opacity-[0.04]" aria-hidden />
            <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-gr-accent/20 blur-3xl" aria-hidden />
            <div className="container-gr grid items-center gap-12 py-16 lg:grid-cols-12 lg:py-28">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 font-mono text-xs uppercase tracking-[0.15em] text-gr-primary dark:text-gr-dprimary"
                    >
                        <span className="h-2 w-2 animate-pulse rounded-full bg-success" /> Gotowe na KSeF FA-2 2026
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.05 }}
                        className="mt-6 text-balance text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
                    >
                        {SITE.slogan}.
                        <span className="block text-gr-primary dark:text-gr-dprimary">Automatyczna i zgodna z przepisami.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.12 }}
                        className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground"
                    >
                        Aplikacja księgowa dla JDG i freelancerów. KSeF, KPiR na autopilocie, OCR faktur i AI Doradca po polsku — wszystko w telefonie.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.18 }}
                        className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
                    >
                        <StoreButton icon={Apple} top="Pobierz z" bottom="App Store" testid="hero-appstore" />
                        <StoreButton icon={Play} top="Dostępne w" bottom="Google Play" testid="hero-googleplay" />
                        <Link
                            to="/features"
                            data-testid="hero-demo"
                            className="rounded-full border border-border px-6 py-3.5 text-center font-semibold text-foreground transition-colors hover:bg-secondary active:scale-95"
                        >
                            Zobacz demo
                        </Link>
                    </motion.div>

                    <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                        {TRUST_BADGES.map((b, i) => {
                            const Icon = ICONS[b.icon];
                            return (
                                <motion.div
                                    key={b.label}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.3 + i * 0.08 }}
                                    className="flex items-start gap-2"
                                    data-testid={`trust-badge-${i}`}
                                >
                                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gr-accent" />
                                    <span className="text-xs font-medium text-muted-foreground">{b.label}</span>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-5"
                >
                    <PhoneMockup />
                </motion.div>
            </div>
        </section>
    );
}
